import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const Section = ({ title, children }) => (
  <section className="mt-12">
    <h2 className="font-display font-extrabold uppercase tracking-normal text-2xl md:text-3xl text-white mb-5">{title}</h2>
    <div className="space-y-4 font-sans text-sm md:text-[15px] text-white/60 leading-relaxed max-w-3xl">{children}</div>
  </section>
);

const List = ({ items }) => (
  <ul className="space-y-2.5 pl-1">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3">
        <span className="mt-2 h-1 w-1 shrink-0 bg-white/40" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function ReturnsPolicy() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
  }, []);

  return (
    <div className="bg-[#050505] min-h-screen grain" data-testid="returns-policy">
      <Navbar />
      <main className="mx-auto max-w-[1100px] px-6 md:px-10 pt-28 md:pt-32 pb-24">
        <button
          data-testid="policy-back"
          onClick={() => navigate("/")}
          className="group flex items-center gap-2 font-sans text-xs tracking-[0.2em] text-white/50 hover:text-white transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
          BACK TO HOME
        </button>

        <motion.header
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="font-sans text-[11px] tracking-[0.45em] text-white/40">LEGAL</span>
          <h1 className="mt-5 font-display font-black uppercase tracking-normal leading-[0.95] text-white text-4xl md:text-6xl">
            Returns, Refunds<br />&amp; Exchanges Policy
          </h1>
          <div className="mt-8 h-px w-16 bg-white/30" />
        </motion.header>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        >
          <Section title="Made-to-Order Products">
            <p>
              Every Lone Wolf Gym Gear product is made specifically for you after your order is placed.
              Because our products are printed on demand, all sales are final.
            </p>
            <p className="text-white/80">We do not accept returns, refunds, or exchanges for:</p>
            <List
              items={[
                "Ordering the wrong size, colour, design, or quantity",
                "Changing your mind after placing an order",
                "Products that do not fit as expected",
                "Minor colour differences caused by lighting, photography, screens, materials, or the printing process",
                "Incorrect or incomplete shipping information supplied by the customer",
                "Products that have been worn, washed, altered, stained, or damaged after delivery",
              ]}
            />
            <p>
              Please carefully review the product description, size chart, colour selection, shipping address,
              and order details before completing your purchase.
            </p>
          </Section>

          <Section title="Damaged, Defective or Incorrect Orders">
            <p className="text-white/80">We will help resolve an order when an item arrives:</p>
            <List
              items={[
                "Damaged",
                "Defective",
                "Incorrectly printed",
                "With a significant printing error",
                "In the wrong size, colour, or design due to a fulfillment error",
                "Different from the item ordered due to a production or shipping mistake",
              ]}
            />
            <p className="text-white/80">
              Please contact us within seven days of delivery whenever possible. Include:
            </p>
            <List
              items={[
                "Your full name",
                "Your order number",
                "A description of the problem",
                "Clear photographs showing the complete item and the issue",
                "A photograph of the shipping label and packaging, when relevant",
              ]}
            />
            <p>We may request additional photographs or information to verify the issue with our production partner.</p>
          </Section>

          <Section title="Approved Claims">
            <p>
              Once the claim has been reviewed, we may provide an appropriate replacement or refund. The available
              resolution will depend on the circumstances and the nature of the production or fulfillment error.
            </p>
            <p>
              Do not return an item without contacting us first. Unauthorized returns may not be accepted or refunded.
            </p>
            <p>
              When a return is required, we will provide the correct return address and return instructions. Products
              should never be returned directly to the address shown on the original package unless we specifically
              instruct you to do so.
            </p>
          </Section>

          <Section title="Order Changes and Cancellations">
            <p>
              Because production may begin shortly after an order is submitted, we cannot guarantee that an order can be
              changed or cancelled.
            </p>
            <p>
              Contact us immediately after ordering if you notice a mistake. Once production has started, the order
              cannot be changed, cancelled, or refunded.
            </p>
          </Section>

          <Section title="Shipping Problems">
            <p className="text-white/80">Lone Wolf Gym Gear is not responsible for delivery problems caused by:</p>
            <List
              items={[
                "An incorrect or incomplete address provided during checkout",
                "A package being refused or left unclaimed",
                "The customer failing to respond to the delivery carrier",
                "Customs delays, import requirements, or unpaid duties",
                "Theft or loss occurring after the carrier marks the package as delivered",
              ]}
            />
            <p>Contact us promptly if your package arrives damaged or if the tracking information indicates a possible delivery problem.</p>
          </Section>

          <Section title="Consumer Rights">
            <p>
              Nothing in this policy limits any consumer right or remedy that cannot legally be excluded under
              applicable law.
            </p>
          </Section>

          <Section title="Contact Us">
            <p>
              For assistance with a damaged, defective, misprinted, or incorrect order, contact us through our
              website's Contact page and include your order number and supporting photographs.
            </p>
            <a
              href="mailto:hello@lonewolfgymgear.com"
              className="inline-flex items-center gap-3 mt-2 border border-white/25 px-7 py-4 font-sans text-xs tracking-[0.25em] text-white hover:bg-white hover:text-black transition-colors duration-300"
            >
              CONTACT SUPPORT
            </a>
          </Section>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
